import React, { useEffect, useRef, useState, useMemo } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { PrimaryButton, SecondaryButton } from "../../globalComponents/ui/Button";
import InfoTip from "../../globalComponents/ui/InfoTip";
import {
  SettingLayout,
  SettingsCard,
  SettingsSectionHeader,
  SettingsFormGroup,
  SettingsFormLabel,
  SettingsDivider,
} from "./SettingShell";
import {
  SectionTitle, RoomyGrid, RoomyInput, Section, CheckRow, CheckItem, Actions, SaveNote,
  Overlay, Dialog, DialogHead, SummaryBody, SummaryGroupLabel, SummaryRow, YearChips,
  DialogActions, EditButtonSlot, AmbitionNote, ConstraintHint, ErrorHint, CollectNote,
  TableWrap, CollectTable, YearTag, S3TableWrap, S3MatrixTable, CellField, LockedCell,
  ActualCell, CoverageRow, CoverageCardBox, CoverageCardTitle, CoverageCardValue,
  CoverageCardDesc, ProgressTrack, ProgressFill, StatusPill, MaterialityBanner, HistoryTable,
  CurrentBadge, Muted, InlineActions, TextButton, SmallInput,
} from "./NetZero.styles";

const API = config.POSTLOGIN_API_URL_COMPANY;

const NET_ZERO_DEADLINE = 2050;
const NEAR_TERM_MAX_YEAR = NET_ZERO_DEADLINE - 1;
const COMMIT_MAX_YEAR = NET_ZERO_DEADLINE - 6;
const NEAR_TERM_OFFSET_MIN = 5;
const NEAR_TERM_OFFSET_MAX = 10;

const EMPTY_FORM = {
  includeScope12: true,
  includeScope3: false,
  baseYearScope12: "",
  baseYearScope3: "",
  collectedEmissionsScope1: {},
  collectedEmissionsScope2: {},
  collectedEmissionsScope3: {},
  nearTermTargetYear: "",
  longTermTargetYear: "",
  netZeroCommitmentYear: "",
  ambitionOverridePercentage: "",
};

const fmtNum = (v) => {
  if (v === "" || v == null) return "";
  const [int, dec] = String(v).split(".");
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec != null ? `${withCommas}.${dec}` : withCommas;
};
const parseNum = (v) => String(v).replace(/[^\d.]/g, "");

const SUBTABS = [
  { key: "settings", label: "Settings", icon: "sliders-h" },
  { key: "history", label: "History", icon: "history" },
];

const EM_SCOPES = ["Scope 1", "Scope 2", "Scope 3"];

function SubNav({ activeKey, onChange }) {
  return (
    <div style={{ display: "flex", borderBottom: "1.5px solid #e2e8f0", marginBottom: 20 }}>
      {SUBTABS.map((tab) => {
        const isActive = activeKey === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px",
              border: "none",
              borderBottom: isActive ? "2.5px solid #3f88a5" : "2.5px solid transparent",
              background: "transparent", color: isActive ? "#3f88a5" : "#6b7280",
              fontSize: 13, fontWeight: isActive ? 700 : 500, fontFamily: "inherit",
              cursor: "pointer", marginBottom: -1.5, outline: "none", whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#1e293b"; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "#6b7280"; }}
          >
            <i className={`fas fa-${tab.icon}`} style={{ fontSize: 12 }} aria-hidden="true" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const fmtAmb = (a) => (a != null ? `${(Number(a) * 100).toFixed(1)}%/yr` : "—");

function HistoryPanel({ onEditConfig }) {
  const [rows, setRows] = useState(null);
  const [collected, setCollected] = useState({});
  const [actuals, setActuals] = useState({});
  const [scope3Cats, setScope3Cats] = useState([]);
  const [emScope, setEmScope] = useState("Scope 1");
  const [emEditing, setEmEditing] = useState(false);
  const [emDraft, setEmDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const loadHistory = async () => {
    const { isSuccess, data } = await apiCall(`${API}netzero/settings/history`, {}, {}, "GET");
    if (!isSuccess) {
      setRows([]);
      setCollected({});
      setActuals({});
    } else {
      setRows(Array.isArray(data) ? data : data?.configs || []);
      setCollected(Array.isArray(data) ? {} : data?.collected || {});
      const a = Array.isArray(data) ? null : data?.actualEmissions;
      setActuals(a ? { "Scope 1": a.scope1 || {}, "Scope 2": a.scope2 || {}, "Scope 3": a.scope3 || {} } : {});
    }
    const s3 = await apiCall(`${API}netzero/scope3-categories`, {}, {}, "GET");
    setScope3Cats(s3?.isSuccess ? s3.data?.categories || [] : []);
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const toggleConfig = async (id, enabled) => {
    setBusy(`config-${id}`);
    setNote("");
    const { isSuccess, message } = await apiCall(`${API}netzero/config/${id}`, {}, { enabled }, "PATCH");
    setBusy("");
    if (isSuccess) {
      setNote(enabled ? "Configuration enabled." : "Configuration disabled.");
      loadHistory();
    } else {
      setNote(message || "Could not update configuration status.");
    }
  };

  const scope3WithData = scope3Cats.filter((c) =>
    Object.values(c.emissions || {}).some((v) => v != null && v !== "" && Number(v) !== 0),
  );
  const emRows =
    emScope === "Scope 3"
      ? scope3WithData.map((c) => ({ key: c.categoryKey, label: c.name, category: c.categoryKey, values: c.emissions || {} }))
      : [{ key: emScope, label: emScope, category: "", values: collected[emScope] || {} }];
  const actualForScope = emScope === "Scope 3" ? {} : actuals[emScope] || {};
  const emYears = Array.from(
    new Set([
      ...emRows.flatMap((r) => Object.keys(r.values || {}).map(Number)),
      ...Object.keys(actualForScope).map(Number),
    ]),
  ).sort((a, b) => a - b);

  const switchScope = (s) => {
    setEmScope(s);
    setEmEditing(false);
    setEmDraft({});
  };

  const startEmEdit = () => {
    const draft = {};
    emRows.forEach((r) =>
      emYears.forEach((y) => {
        const v = r.values[y];
        draft[`${r.category}-${y}`] = v != null ? String(v) : "";
      }),
    );
    setEmDraft(draft);
    setEmEditing(true);
    setNote("");
  };

  const cancelEmEdit = () => {
    setEmEditing(false);
    setEmDraft({});
  };

  const saveEmBulk = async () => {
    const entries = [];
    emRows.forEach((r) =>
      emYears.forEach((y) => {
        const raw = emDraft[`${r.category}-${y}`];
        if (raw !== "" && raw != null && !Number.isNaN(Number(raw))) {
          entries.push({ scope: emScope, year: Number(y), category: r.category, value: Number(raw) });
        }
      }),
    );
    setBusy("bulk");
    setNote("");
    const { isSuccess, message } = await apiCall(`${API}netzero/collected-emission/bulk`, {}, { entries }, "PATCH");
    setBusy("");
    if (isSuccess) {
      setEmEditing(false);
      setEmDraft({});
      setNote("Emissions updated.");
      loadHistory();
    } else {
      setNote(message || "Could not update emissions.");
    }
  };

  return (
    <>
      <SettingsCard style={{ padding: "20px 24px", overflowX: "auto" }}>
        <SettingsSectionHeader>
          <SectionTitle>Configurations</SectionTitle>
        </SettingsSectionHeader>
        {rows === null ? (
          <Muted>Loading…</Muted>
        ) : rows.length === 0 ? (
          <Muted>No history yet — save a configuration to start the history.</Muted>
        ) : (
          <HistoryTable>
            <thead>
              <tr>
                <th>Effective From</th>
                <th>Effective To</th>
                <th>Base Yr (S1&2)</th>
                <th>Base Yr (S3)</th>
                <th>Near-Term</th>
                <th>Net-Zero</th>
                <th>Ambition</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.effectiveFrom)}</td>
                  <td>{r.effectiveTo ? fmtDate(r.effectiveTo) : <CurrentBadge>Current</CurrentBadge>}</td>
                  <td>{r.baseYearScope12 ?? "—"}</td>
                  <td>{r.baseYearScope3 ?? "—"}</td>
                  <td>{r.nearTermTargetYear ?? "—"}</td>
                  <td>{r.longTermTargetYear ?? "—"}</td>
                  <td>{fmtAmb(r.ambitionOverridePercentage)}</td>
                  <td>{r.status}</td>
                  <td>
                    <InlineActions>
                      {r.status === "ACTIVE" ? (
                        <>
                          <TextButton type="button" onClick={() => onEditConfig(r)}>Edit</TextButton>
                          <TextButton type="button" style={{ color: "#c0392b" }} disabled={busy === `config-${r.id}`} onClick={() => toggleConfig(r.id, false)}>
                            Disable
                          </TextButton>
                        </>
                      ) : (
                        <TextButton type="button" style={{ color: "#3f8a5c" }} disabled={busy === `config-${r.id}`} onClick={() => toggleConfig(r.id, true)}>
                          Enable
                        </TextButton>
                      )}
                    </InlineActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        )}
      </SettingsCard>

      <SettingsCard style={{ padding: "20px 24px", marginTop: 18 }}>
        <SettingsSectionHeader style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionTitle style={{ margin: 0 }}>Entered Emissions</SectionTitle>
          {emRows.length > 0 && emYears.length > 0 && (
            emEditing ? (
              <InlineActions>
                <TextButton type="button" disabled={busy === "bulk"} onClick={saveEmBulk}>Save all</TextButton>
                <TextButton type="button" onClick={cancelEmEdit}>Cancel</TextButton>
              </InlineActions>
            ) : (
              <TextButton type="button" onClick={startEmEdit}>Edit</TextButton>
            )
          )}
        </SettingsSectionHeader>

        <div style={{ display: "flex", borderBottom: "1.5px solid #e2e8f0", margin: "12px 0 16px" }}>
          {EM_SCOPES.map((s) => {
            const isActive = emScope === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => switchScope(s)}
                style={{
                  padding: "8px 16px", border: "none",
                  borderBottom: isActive ? "2.5px solid #3f88a5" : "2.5px solid transparent",
                  background: "transparent", color: isActive ? "#3f88a5" : "#6b7280",
                  fontSize: 13, fontWeight: isActive ? 700 : 500, fontFamily: "inherit",
                  cursor: "pointer", marginBottom: -1.5, whiteSpace: "nowrap", outline: "none",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#1e293b"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "#6b7280"; }}
              >
                {s}
              </button>
            );
          })}
        </div>

        {emRows.length === 0 || emYears.length === 0 ? (
          <Muted>No manually entered {emScope} emissions yet.</Muted>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <HistoryTable style={{ minWidth: 220 + emYears.length * 150 }}>
            <thead>
              <tr>
                <th style={{ position: "sticky", left: 0, background: "#f8fafc" }}>
                  {emScope === "Scope 3" ? "Category" : "Scope"}
                </th>
                {emYears.map((y) => (
                  <th key={y} style={{ textAlign: "right" }}>{y}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {emRows.map((r) => (
                <tr key={r.key}>
                  <td style={{ position: "sticky", left: 0, background: "#fff", fontWeight: 600 }}>
                    {emScope === "Scope 3" && (
                      <span style={{ color: "#94a3b8", marginRight: 6, fontVariantNumeric: "tabular-nums" }}>
                        {r.category.replace("CAT_", "").padStart(2, "0")}
                      </span>
                    )}
                    {r.label}
                  </td>
                  {emYears.map((y) => {
                    const cellKey = `${r.category}-${y}`;
                    const stored = r.values[y];
                    const actual = r.category === "" ? actualForScope[y] : undefined;
                    return (
                      <td key={y} style={{ textAlign: "right", background: actual != null ? "#f4f9fb" : undefined }}>
                        {actual != null ? (
                          <span
                            title="Actual emissions from your ESG dashboard — not editable"
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 6, color: "#2c6a82", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                          >
                            <i className="fas fa-lock" style={{ fontSize: 10, color: "#9cbccb" }} aria-hidden="true" />
                            {Number(actual).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                          </span>
                        ) : emEditing ? (
                          <SmallInput
                            type="text"
                            inputMode="numeric"
                            value={fmtNum(emDraft[cellKey] ?? "")}
                            onChange={(e) => {
                              const val = parseNum(e.target.value);
                              setEmDraft((d) => ({ ...d, [cellKey]: val }));
                            }}
                          />
                        ) : stored != null && stored !== "" ? (
                          Number(stored).toLocaleString(undefined, { maximumFractionDigits: 4 })
                        ) : (
                          "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </HistoryTable>
          </div>
        )}
      </SettingsCard>
      {note && <div style={{ marginTop: 12, fontSize: 13, color: "#475569" }}>{note}</div>}
    </>
  );
}

const settingToForm = (s, collected = {}) => ({
  includeScope12: s.baseYearScope12 != null,
  includeScope3: s.baseYearScope3 != null,
  baseYearScope12: s.baseYearScope12 ?? "",
  baseYearScope3: s.baseYearScope3 ?? "",
  collectedEmissionsScope1: collected["Scope 1"] || s.collectedEmissionsScope1 || {},
  collectedEmissionsScope2: collected["Scope 2"] || s.collectedEmissionsScope2 || {},
  collectedEmissionsScope3: collected["Scope 3"] || s.collectedEmissionsScope3 || {},
  nearTermTargetYear: s.nearTermTargetYear ?? "",
  longTermTargetYear: s.longTermTargetYear ?? "",
  netZeroCommitmentYear: s.netZeroCommitmentYear ?? "",
  ambitionOverridePercentage:
    s.ambitionOverridePercentage != null ? Number(s.ambitionOverridePercentage) * 100 : "",
});

const windowEndFor = (dataYearsArrays, currentYear) => {
  const all = dataYearsArrays.flat().filter((y) => y != null);
  return all.length ? Math.max(...all) : currentYear;
};

const gapYearsFor = (baseYear, dataYears, windowEnd) => {
  if (!baseYear || baseYear < 2015 || !windowEnd) return [];
  const set = new Set(dataYears);
  const gaps = [];
  for (let y = baseYear; y <= windowEnd; y++) if (!set.has(y)) gaps.push(y);
  return gaps;
};

const NetZero = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [availableYears, setAvailableYears] = useState({ scope1: [], scope2: [], scope3: [] });
  const [actualEmissions, setActualEmissions] = useState({ scope1: {}, scope2: {}, scope3: {} });
  const [ambitionFloor, setAmbitionFloor] = useState(null);
  const [hasConfig, setHasConfig] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("settings");
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [s3Categories, setS3Categories] = useState([]);
  const [s1BaseEmissions, setS1BaseEmissions] = useState(0);

  const prevCommitRef = useRef(undefined);

  const currentYear = new Date().getFullYear();
  const s12WindowEnd = windowEndFor([availableYears.scope1, availableYears.scope2], currentYear);
  const s3WindowEnd = windowEndFor([availableYears.scope3], currentYear);

  const loadSettings = async () => {
    const { isSuccess, data } = await apiCall(`${API}netzero/settings`, {}, {}, "GET");
    if (isSuccess && data) {
      setAvailableYears(data.availableYears || { scope1: [], scope2: [], scope3: [] });
      setActualEmissions(data.actualEmissions || { scope1: {}, scope2: {}, scope3: {} });
      if (data.setting) {
        setForm(settingToForm(data.setting, data.collectedEmissions));
        setHasConfig(true);
        setEditing(false);
      } else {
        setForm(EMPTY_FORM);
        setHasConfig(false);
        setEditing(true);
      }
    }
  };

  const loadS3Categories = async () => {
    const { isSuccess, data } = await apiCall(`${API}netzero/scope3-categories`, {}, {}, "GET");
    if (isSuccess && data) {
      setS3Categories(data.categories || []);
    }
  };

  useEffect(() => {
    loadSettings();
    loadS3Categories();
  }, []);

  useEffect(() => {
    if (hasConfig) {
      apiCall(`${API}netzero/scope1`, {}, {}, "GET").then((res) => {
        if (res?.isSuccess && res.data) {
          setS1BaseEmissions(res.data.baseEmissions || 0);
        }
      });
    }
  }, [hasConfig]);

  useEffect(() => {
    if (!editing || !form.includeScope12) return;
    const by = Number(form.baseYearScope12);
    const nt = Number(form.nearTermTargetYear);
    const nz = Number(form.longTermTargetYear);
    if (!by || !nt || !nz) return;
    let cancelled = false;
    (async () => {
      const body = { baseYearScope12: by, nearTermTargetYear: nt, netZeroYear: nz };
      const b1 = form.collectedEmissionsScope1[by];
      const b2 = form.collectedEmissionsScope2[by];
      if (b1 !== undefined && b1 !== "" && b2 !== undefined && b2 !== "") {
        body.baseEmissionsScope12 = Number(b1) + Number(b2);
      }
      const { isSuccess, data } = await apiCall(`${API}netzero/ambition-floor`, {}, body, "POST", true, false);
      if (cancelled || !isSuccess || !data) return;
      setAmbitionFloor(data);
      setForm((f) => {
        const floor = Number(Number(data.floorRatePct).toFixed(2));
        const cur = f.ambitionOverridePercentage;
        return cur === "" || Number(cur) < floor ? { ...f, ambitionOverridePercentage: floor } : f;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, form.includeScope12, form.baseYearScope12, form.nearTermTargetYear, form.longTermTargetYear, form.collectedEmissionsScope1, form.collectedEmissionsScope2]);

  useEffect(() => {
    if (!editing || !form.includeScope12) {
      prevCommitRef.current = undefined;
      return;
    }
    const commit = form.netZeroCommitmentYear !== "" ? Number(form.netZeroCommitmentYear) : null;
    const recommended =
      commit != null && commit >= 2015 && commit <= COMMIT_MAX_YEAR ? commit + NEAR_TERM_OFFSET_MIN : "";
    const commitChanged =
      prevCommitRef.current !== undefined && prevCommitRef.current !== form.netZeroCommitmentYear;
    prevCommitRef.current = form.netZeroCommitmentYear;
    setForm((f) => {
      let next = f;
      if (f.nearTermTargetYear === "" || commitChanged) {
        next = { ...next, nearTermTargetYear: recommended };
      }
      if (f.longTermTargetYear === "") next = { ...next, longTermTargetYear: NET_ZERO_DEADLINE };
      return next;
    });
  }, [editing, form.includeScope12, form.netZeroCommitmentYear]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCollected = (mapKey, year, value) =>
    setForm((f) => ({ ...f, [mapKey]: { ...f[mapKey], [year]: value } }));
  const toggleScope12 = (checked) =>
    setForm((f) => ({ ...f, includeScope12: checked, includeScope3: checked ? f.includeScope3 : false }));
  const toggleScope3 = (checked) =>
    setForm((f) => ({ ...f, includeScope3: checked, includeScope12: checked ? true : f.includeScope12 }));
  const toNum = (v) => (v === "" || v === null ? undefined : Number(v));

  const isBlank = (v) => v === "" || v === null || v === undefined;

  const s3Totals = useMemo(() => {
    const by3 = form.baseYearScope3 ? Number(form.baseYearScope3) : null;
    const totalBase = s3Categories.reduce((sum, c) => {
      const val = by3 != null ? Number(c.emissions[by3] ?? 0) : 0;
      return sum + val;
    }, 0);
    const ntBase = s3Categories.filter((c) => c.includeNt).reduce((sum, c) => {
      const val = by3 != null ? Number(c.emissions[by3] ?? 0) : 0;
      return sum + val;
    }, 0);
    const ltBase = s3Categories.filter((c) => c.includeLt).reduce((sum, c) => {
      const val = by3 != null ? Number(c.emissions[by3] ?? 0) : 0;
      return sum + val;
    }, 0);
    const ntPct = totalBase > 0 ? (ntBase / totalBase) * 100 : 0;
    const ltPct = totalBase > 0 ? (ltBase / totalBase) * 100 : 0;
    return { totalBase, ntBase, ltBase, ntPct, ltPct };
  }, [s3Categories, form.baseYearScope3]);

  const s12BaseTotal = useMemo(() => {
    const by12 = form.baseYearScope12 ? Number(form.baseYearScope12) : null;
    if (!by12) return s1BaseEmissions;
    const s1 = form.collectedEmissionsScope1[by12] !== undefined ? Number(form.collectedEmissionsScope1[by12]) : 0;
    const s2 = form.collectedEmissionsScope2[by12] !== undefined ? Number(form.collectedEmissionsScope2[by12]) : 0;
    return (s1 || s1BaseEmissions) + s2;
  }, [form.baseYearScope12, form.collectedEmissionsScope1, form.collectedEmissionsScope2, s1BaseEmissions]);

  const scope3Share = useMemo(() => {
    const totalFootprint = s12BaseTotal + s3Totals.totalBase;
    return totalFootprint > 0 ? (s3Totals.totalBase / totalFootprint) * 100 : 0;
  }, [s12BaseTotal, s3Totals.totalBase]);

  const scope3Material = scope3Share >= 40;
  const nearTermRequired = scope3Material;
  const ntPass = s3Totals.ntPct >= 67;
  const ltPass = s3Totals.ltPct >= 90;

  const handleEmissionChange = (categoryKey, y, val) => {
    const baseYear = form.baseYearScope3 ? Number(form.baseYearScope3) : null;
    const isBaseYear = baseYear != null && Number(y) === baseYear;
    const num = val === "" ? 0 : Number(val);
    setS3Categories((prev) =>
      prev.map((c) => {
        if (c.categoryKey !== categoryKey) return c;
        if (isBaseYear && num === 0) {
          const zeroed = {};
          Object.keys(c.emissions).forEach((k) => { zeroed[k] = 0; });
          return { ...c, emissions: zeroed };
        }
        return { ...c, emissions: { ...c.emissions, [y]: num } };
      })
    );
  };

  const handleCategoryToggle = (categoryKey, field, checked) => {
    setS3Categories((prev) =>
      prev.map((c) =>
        c.categoryKey === categoryKey
          ? {
              ...c,
              [field]: checked,
            }
          : c
      )
    );
  };

  const handleSave = async () => {
    setMessage("");

    if (form.includeScope12) {
      if ([form.baseYearScope12, form.nearTermTargetYear, form.longTermTargetYear, form.netZeroCommitmentYear, form.ambitionOverridePercentage].some(isBlank)) {
        setMessage("Please fill all Scope 1, Scope 2 fields.");
        return;
      }
      const by = Number(form.baseYearScope12);
      if (by < 2015 || by > currentYear) {
        setMessage(`Base year must be between 2015 and ${currentYear}.`);
        return;
      }
      const missing1 = gapYearsFor(by, availableYears.scope1, s12WindowEnd).filter((y) => isBlank(form.collectedEmissionsScope1[y]));
      const missing2 = gapYearsFor(by, availableYears.scope2, s12WindowEnd).filter((y) => isBlank(form.collectedEmissionsScope2[y]));
      if (missing1.length || missing2.length) {
        const parts = [];
        if (missing1.length) parts.push(`Scope 1: ${missing1.join(", ")}`);
        if (missing2.length) parts.push(`Scope 2: ${missing2.join(", ")}`);
        setMessage(`Enter emissions for the years without data — ${parts.join("; ")}.`);
        return;
      }
      const commit = Number(form.netZeroCommitmentYear);
      if (commit < by) {
        setMessage("Net-zero commitment year must be on or after the base year.");
        return;
      }
      if (commit > COMMIT_MAX_YEAR) {
        setMessage(`Net-zero commitment year must be ${COMMIT_MAX_YEAR} or earlier so the near-term target lands before ${NET_ZERO_DEADLINE}.`);
        return;
      }
      const nt = Number(form.nearTermTargetYear);
      const ntMax = Math.min(commit + NEAR_TERM_OFFSET_MAX, NEAR_TERM_MAX_YEAR);
      if (nt < commit + NEAR_TERM_OFFSET_MIN || nt > ntMax) {
        setMessage(`Near-term target year must be between ${commit + NEAR_TERM_OFFSET_MIN} and ${ntMax}.`);
        return;
      }
      if (Number(form.longTermTargetYear) <= nt || Number(form.longTermTargetYear) > 2050) {
        setMessage(`Net-zero target year must be after ${nt} and no later than 2050.`);
        return;
      }
      if (ambitionFloor && Number(form.ambitionOverridePercentage) < Number(ambitionFloor.floorRatePct) - 1e-6) {
        setMessage(`Ambition rate must be at least ${Number(ambitionFloor.floorRatePct).toFixed(2)}%/yr.`);
        return;
      }
    }
    if (form.includeScope3) {
      if (isBlank(form.baseYearScope3)) {
        setMessage("Please enter the Scope 3 base year.");
        return;
      }
      const by3 = Number(form.baseYearScope3);
      if (by3 < 2015 || by3 > currentYear) {
        setMessage(`Scope 3 base year must be between 2015 and ${currentYear}.`);
        return;
      }
      if (form.includeScope12 && !isBlank(form.baseYearScope12) && by3 < Number(form.baseYearScope12)) {
        setMessage(`Scope 3 base year must be on or after the Scope 1, Scope 2 base year (${form.baseYearScope12}).`);
        return;
      }
      
      const s3YearsList = getS3YearsList();
      for (const c of s3Categories) {
        const isChecked = (nearTermRequired && c.includeNt) || c.includeLt;
        if (isChecked) {
          const missingYears = s3YearsList.filter((y) => isBlank(c.emissions[y]));
          if (missingYears.length > 0) {
            const catNum = c.categoryKey.replace("CAT_", "").padStart(2, "0");
            setMessage(`Please enter emissions for all years (${missingYears.join(", ")}) for category ${catNum} ${c.name} since it is included in targets.`);
            return;
          }
        }
      }
    }

    setShowConfirm(true);
  };

  const confirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    const collect = (mapKey, gaps) => {
      const out = {};
      for (const y of gaps) out[y] = Number(form[mapKey][y]);
      return out;
    };
    const payload = {};
    if (form.includeScope12) {
      payload.baseYearScope12 = toNum(form.baseYearScope12);
      payload.nearTermTargetYear = toNum(form.nearTermTargetYear);
      payload.longTermTargetYear = toNum(form.longTermTargetYear);
      payload.netZeroCommitmentYear = toNum(form.netZeroCommitmentYear);
      payload.ambitionOverridePercentage =
        form.ambitionOverridePercentage === "" ? undefined : Number(form.ambitionOverridePercentage) / 100;
      const gap1 = gapYearsFor(Number(form.baseYearScope12), availableYears.scope1, s12WindowEnd);
      const gap2 = gapYearsFor(Number(form.baseYearScope12), availableYears.scope2, s12WindowEnd);
      if (gap1.length) payload.collectedEmissionsScope1 = collect("collectedEmissionsScope1", gap1);
      if (gap2.length) payload.collectedEmissionsScope2 = collect("collectedEmissionsScope2", gap2);
    }
    if (form.includeScope3) {
      payload.baseYearScope3 = toNum(form.baseYearScope3);
    }
    
    const res = await apiCall(`${API}netzero/settings`, {}, payload, "POST");
    if (!res.isSuccess) {
      setSaving(false);
      setMessage(res.message || "Could not save — please check the values and try again.");
      return;
    }

    if (form.includeScope3) {
      const categoriesPayload = s3Categories.map((c) => {
        const emissions = { ...c.emissions };
        if (form.baseYearScope3) {
          emissions[form.baseYearScope3] = Number(c.emissions[form.baseYearScope3] || 0);
        }
        const gap3 = gapYearsFor(Number(form.baseYearScope3), availableYears.scope3, s3WindowEnd);
        gap3.forEach((y) => {
          emissions[y] = Number(c.emissions[y] || 0);
        });
        return {
          categoryKey: c.categoryKey,
          emissions,
          includeNt: c.includeNt,
          includeLt: c.includeLt,
        };
      });

      const catRes = await apiCall(
        `${API}netzero/scope3-categories`,
        {},
        { categories: categoriesPayload },
        "PUT"
      );
      if (!catRes.isSuccess) {
        setSaving(false);
        setMessage(catRes.message || "Settings saved, but could not save Scope 3 categories.");
        return;
      }
    }

    setSaving(false);
    setMessage("Saved successfully.");
    await loadSettings();
    await loadS3Categories();
  };

  const handleCancel = () => {
    setMessage("");
    loadSettings();
    loadS3Categories();
  };

  const handleEditHistoryConfig = (setting) => {
    setMessage("");
    setForm(settingToForm(setting, {
      "Scope 1": form.collectedEmissionsScope1,
      "Scope 2": form.collectedEmissionsScope2,
    }));

    if (setting.scope3Categories) {
      const toggles = new Map(setting.scope3Categories.map((c) => [c.key, c]));
      setS3Categories((prev) =>
        prev.map((c) => {
          const t = toggles.get(c.categoryKey);
          return {
            ...c,
            includeNt: t ? Boolean(t.includeNt) : false,
            includeLt: t ? Boolean(t.includeLt) : false,
          };
        })
      );
    }

    setHasConfig(true);
    setEditing(true);
    setActiveSubTab("settings");
  };

  const ro = !editing;

  const yearField = (label, key, hint, constraint, disabled, disabledMsg, error) => {
    const showError = !ro && !disabled && !!error;
    return (
      <SettingsFormGroup>
        <SettingsFormLabel>{label}<InfoTip text={hint} /></SettingsFormLabel>
        <RoomyInput
          type="number"
          min={2015}
          value={form[key]}
          placeholder={!ro && !disabled ? constraint : ""}
          onChange={(e) => setField(key, e.target.value.replace(/[^\d]/g, ""))}
          readOnly={ro || disabled}
          disabled={!ro && disabled}
          $error={showError}
        />
        {!ro && (
          disabled ? <ConstraintHint>{disabledMsg}</ConstraintHint>
            : showError ? <ErrorHint>{error}</ErrorHint>
              : null
        )}
      </SettingsFormGroup>
    );
  };

  const ambitionHint =
    `Auto-calculated: the SBTi minimum, raised by FLA where it applies. You can enter a higher rate, never lower.`;

  const byNum = form.baseYearScope12 !== "" ? Number(form.baseYearScope12) : null;
  const gapYears1 = form.includeScope12 && byNum ? gapYearsFor(byNum, availableYears.scope1, s12WindowEnd) : [];
  const gapYears2 = form.includeScope12 && byNum ? gapYearsFor(byNum, availableYears.scope2, s12WindowEnd) : [];
  const gapYearsUnion = Array.from(new Set([...gapYears1, ...gapYears2])).sort((a, b) => a - b);
  const gapYears3 = form.includeScope3 && form.baseYearScope3 !== "" ? gapYearsFor(Number(form.baseYearScope3), availableYears.scope3, s3WindowEnd) : [];

  const baseYearValid = byNum != null && byNum >= 2015 && byNum <= currentYear;
  const commitNum = form.netZeroCommitmentYear !== "" ? Number(form.netZeroCommitmentYear) : null;
  const commitValid = baseYearValid && commitNum != null && commitNum >= byNum && commitNum <= COMMIT_MAX_YEAR;
  const nearTermMin = commitNum != null ? commitNum + NEAR_TERM_OFFSET_MIN : null;
  const nearTermMax = commitNum != null ? Math.min(commitNum + NEAR_TERM_OFFSET_MAX, NEAR_TERM_MAX_YEAR) : null;
  const nearTermValid =
    commitValid && form.nearTermTargetYear !== "" &&
    Number(form.nearTermTargetYear) >= nearTermMin && Number(form.nearTermTargetYear) <= nearTermMax;

  const commitHint = "The year you publicly pledged to reach net zero. Your near-term target is set 5–10 years from here.";
  const commitConstraint = `${form.baseYearScope12 || "base year"} – ${COMMIT_MAX_YEAR}`;
  const nearTermHint = `SBTi requires the near-term target 5–10 years after your commitment year (recommended +5), and before the ${NET_ZERO_DEADLINE} net-zero year.`;
  const nearTermConstraint = commitValid ? `${nearTermMin} – ${nearTermMax} (recommended ${nearTermMin})` : "commitment year + 5 to +10";
  const nearTermDisabledMsg =
    commitNum == null ? "Enter the commitment year first."
      : commitNum < byNum ? "Commitment year must be on or after the base year."
        : commitNum > COMMIT_MAX_YEAR ? `Commitment year must be ${COMMIT_MAX_YEAR} or earlier so the near-term lands before ${NET_ZERO_DEADLINE}.`
          : "Enter the commitment year first.";
  const netZeroHint = `Net zero by ${NET_ZERO_DEADLINE} (SBTi deadline) — a ~90% cut with the remainder neutralised. Must be after the near-term year.`;
  const netZeroConstraint = form.nearTermTargetYear !== "" ? `${Number(form.nearTermTargetYear) + 1} – ${NET_ZERO_DEADLINE}` : `up to ${NET_ZERO_DEADLINE}`;

  const by3 = form.baseYearScope3 !== "" ? Number(form.baseYearScope3) : null;
  const baseYearError =
    form.baseYearScope12 === "" || baseYearValid ? null : `Base year must be between 2015 and ${currentYear}.`;
  const baseYear3Error =
    by3 == null ? null
      : by3 < 2015 || by3 > currentYear ? `Base year must be between 2015 and ${currentYear}.`
        : form.includeScope12 && form.baseYearScope12 !== "" && by3 < Number(form.baseYearScope12)
          ? `Must be on or after the Scope 1, Scope 2 base year (${form.baseYearScope12}).`
          : null;
  const commitError =
    form.netZeroCommitmentYear === "" || !baseYearValid ? null
      : commitNum < byNum ? `Must be on or after the base year (${byNum}).`
        : commitNum > COMMIT_MAX_YEAR ? `Must be ${COMMIT_MAX_YEAR} or earlier so the near-term lands before ${NET_ZERO_DEADLINE}.`
          : null;
  const nearTermError =
    form.nearTermTargetYear === "" || !commitValid ? null
      : Number(form.nearTermTargetYear) < nearTermMin || Number(form.nearTermTargetYear) > nearTermMax
        ? `Must be between ${nearTermMin} and ${nearTermMax}.`
        : null;
  const netZeroError =
    form.longTermTargetYear === "" || !nearTermValid ? null
      : Number(form.longTermTargetYear) <= Number(form.nearTermTargetYear) || Number(form.longTermTargetYear) > NET_ZERO_DEADLINE
        ? `Must be after ${form.nearTermTargetYear} and no later than ${NET_ZERO_DEADLINE}.`
        : null;
  const hasFieldErrors = [
    form.includeScope12 && baseYearError,
    form.includeScope3 && baseYear3Error,
    form.includeScope12 && commitError,
    form.includeScope12 && nearTermError,
    form.includeScope12 && netZeroError,
  ].some(Boolean);

  const collectCell = (mapKey, y, needed, actualsForScope = {}) => {
    if (needed) {
      return (
        <CellField>
          <input
            type="text"
            inputMode="numeric"
            placeholder="0"
            value={fmtNum(form[mapKey][y])}
            onChange={(e) => setCollected(mapKey, y, parseNum(e.target.value))}
            readOnly={ro}
          />
          <span className="unit">tCO₂e</span>
        </CellField>
      );
    }
    const actual = actualsForScope[y];
    if (actual == null) return <LockedCell>Has data</LockedCell>;
    return (
      <ActualCell title="Actual emissions from your ESG dashboard — not editable">
        <i className="fas fa-lock lock" aria-hidden="true" />
        <span className="amt">{fmtNum(actual)}</span>
        <span className="unit">tCO₂e</span>
      </ActualCell>
    );
  };

  const yearTag = (y, baseYear) => {
    if (baseYear != null && Number(baseYear) === y) return "Base year";
    if (commitNum != null && commitNum === y) return "Commitment year";
    return null;
  };

  const s12CollectionSection = () =>
    gapYearsUnion.length > 0 ? (
      <Section>
        <SettingsSectionHeader>
          <SectionTitle>Emissions for years without data (Scope 1, Scope 2)</SectionTitle>
        </SettingsSectionHeader>
        {(!gapYears1.every((y) => !isBlank(form.collectedEmissionsScope1[y])) ||
          !gapYears2.every((y) => !isBlank(form.collectedEmissionsScope2[y]))) && (
          <CollectNote>
            There's no emissions data for {gapYearsUnion[0]}{gapYearsUnion.length > 1 ? `–${gapYearsUnion[gapYearsUnion.length - 1]}` : ""}.
            Enter the total Scope 1 and Scope 2 emissions for each year — these are stored as the actuals
            and drive the whole calculation.
          </CollectNote>
        )}
        <TableWrap style={{ overflowX: "auto" }}>
          <CollectTable style={{ width: "auto", minWidth: 160 + gapYearsUnion.length * 170 }}>
            <colgroup>
              <col style={{ width: 160 }} />
              {gapYearsUnion.map((y) => (
                <col key={y} style={{ width: 170 }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>Scope</th>
                {gapYearsUnion.map((y) => {
                  const tag = yearTag(y, byNum);
                  return (
                    <th key={y} className="num">
                      {y}
                      {tag && <YearTag>{tag}</YearTag>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="year">Scope 1</td>
                {gapYearsUnion.map((y) => (
                  <td key={y}>{collectCell("collectedEmissionsScope1", y, gapYears1.includes(y), actualEmissions.scope1)}</td>
                ))}
              </tr>
              <tr>
                <td className="year">Scope 2</td>
                {gapYearsUnion.map((y) => (
                  <td key={y}>{collectCell("collectedEmissionsScope2", y, gapYears2.includes(y), actualEmissions.scope2)}</td>
                ))}
              </tr>
            </tbody>
          </CollectTable>
        </TableWrap>
      </Section>
    ) : null;

  const getS3YearsList = () => {
    const by3 = form.baseYearScope3 ? Number(form.baseYearScope3) : null;
    if (!by3) return [];
    const gaps = gapYearsFor(by3, availableYears.scope3, s3WindowEnd);
    return Array.from(new Set([by3, ...gaps])).sort((a, b) => a - b);
  };

  const s3CategoryEmissionsSection = () => {
    if (!form.includeScope3) return null;

    const s3YearsList = getS3YearsList();
    if (s3YearsList.length === 0) {
      return (
        <Section>
          <SettingsDivider style={{ margin: "34px 0" }} />
          <SettingsSectionHeader>
            <SectionTitle>Category Emissions & Targets (Scope 3)</SectionTitle>
          </SettingsSectionHeader>
          <div style={{ padding: "16px", color: "#64748b", fontSize: "14px", border: "1px dashed #cbd5e1", borderRadius: "8px", textAlign: "center", background: "#f8fafc" }}>
            Please enter a valid Scope 3 Base Year above first.
          </div>
        </Section>
      );
    }

    const yearlyTotals = {};
    s3YearsList.forEach((y) => {
      yearlyTotals[y] = s3Categories.reduce((sum, c) => sum + Number(c.emissions[y] ?? 0), 0);
    });

    return (
      <Section>
        <SettingsDivider style={{ margin: "34px 0" }} />
        
        <SettingsSectionHeader style={{ marginBottom: 16 }}>
          <SectionTitle style={{ margin: 0 }}>Category Emissions & Targets (Scope 3)</SectionTitle>
        </SettingsSectionHeader>

        <MaterialityBanner $material={scope3Material}>
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: scope3Material ? "#3f88a5" : "#cbd5e1",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 1,
              fontStyle: "italic",
              fontFamily: "Georgia, serif",
            }}
          >
            i
          </span>
          <div style={{ fontSize: "13.5px", lineHeight: "1.5" }}>
            <span style={{ fontWeight: 700 }}>
              Your Scope 3 is {scope3Share.toFixed(0)}% of total emissions —{" "}
            </span>
            <span style={{ color: "#64748b" }}>
              {scope3Material
                ? "SBTi requires a near-term Scope 3 target when Scope 3 reaches 40% of your footprint."
                : "a near-term Scope 3 target is optional below the 40% materiality threshold."}
            </span>
          </div>
        </MaterialityBanner>

        {gapYears3.length > 0 && (
          <CollectNote style={{ marginBottom: 20, padding: "12px 16px", borderRadius: 8, fontSize: 13.5 }}>
            <i className="fas fa-info-circle" style={{ marginRight: 8, color: "#3f88a5" }} />
            There is no Scope 3 emissions data in the system for <b>{gapYears3[0]}–{gapYears3[gapYears3.length - 1]}</b>.
            Please fill in the category emissions for each year in the grid below.
          </CollectNote>
        )}

        <CoverageRow>
          {nearTermRequired ? (
            <CoverageCardBox>
              <CoverageCardTitle>
                Near-term coverage
                <InfoTip text="At least 67% of your Scope 3 base emissions, by category, must be covered by your near-term target." />
              </CoverageCardTitle>
              <CoverageCardValue>
                {s3Totals.ntPct.toFixed(0)}%
                <StatusPill $pass={ntPass}>{ntPass ? "✓ On track" : "✗ Below target"}</StatusPill>
              </CoverageCardValue>
              <CoverageCardDesc>
                {ntPass
                  ? "Meets the ≥67% requirement"
                  : "Add more categories to reach 67%"}
              </CoverageCardDesc>
              <ProgressTrack>
                <ProgressFill $pct={s3Totals.ntPct} $pass={ntPass} />
              </ProgressTrack>
            </CoverageCardBox>
          ) : (
            <CoverageCardBox style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <CoverageCardTitle>Near-term coverage</CoverageCardTitle>
              <div style={{ fontSize: "13px", color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>
                Not required — Scope 3 is under 40% of your total emissions.
              </div>
            </CoverageCardBox>
          )}

          <CoverageCardBox>
            <CoverageCardTitle>
              Long-term coverage
              <InfoTip text="At least 90% of your Scope 3 base emissions, by category, must be covered by your long-term (net-zero) target." />
            </CoverageCardTitle>
            <CoverageCardValue>
              {s3Totals.ltPct.toFixed(0)}%
              <StatusPill $pass={ltPass}>{ltPass ? "✓ On track" : "✗ Below target"}</StatusPill>
            </CoverageCardValue>
            <CoverageCardDesc>
              {ltPass
                ? "Meets the ≥90% requirement"
                : "Add more categories to reach 90%"}
            </CoverageCardDesc>
            <ProgressTrack>
              <ProgressFill $pct={s3Totals.ltPct} $pass={ltPass} />
            </ProgressTrack>
          </CoverageCardBox>

          <CoverageCardBox style={{ flex: "0 0 180px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <CoverageCardTitle>Total Scope 3 Base</CoverageCardTitle>
            <div style={{ fontSize: "18px", fontWeight: 700, marginTop: 5, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>
              {fmtNum(s3Totals.totalBase)}
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: 3 }}>tCO₂e, base year {form.baseYearScope3}</div>
          </CoverageCardBox>
        </CoverageRow>

        <S3TableWrap>
          <S3MatrixTable>
            <thead>
              <tr>
                {nearTermRequired && <th className="sticky-col-nt">NT</th>}
                <th className="sticky-col-lt" style={{ left: nearTermRequired ? 45 : 0 }}>LT</th>
                <th className="sticky-col-cat" style={{ textAlign: "left", left: nearTermRequired ? 90 : 45 }}>Category</th>
                {s3YearsList.map((y) => (
                  <th key={y} className={y === Number(form.baseYearScope3) ? "base-year-col" : ""} style={{ textAlign: "right", width: 130, minWidth: 130 }}>
                    {y === Number(form.baseYearScope3) ? `${y} (Base Year)` : y}
                  </th>
                ))}
                {nearTermRequired && <th style={{ textAlign: "right", width: 120, minWidth: 120 }}>NT Target</th>}
                <th style={{ textAlign: "right", width: 120, minWidth: 120 }}>LT Target</th>
              </tr>
            </thead>
            <tbody>
              {s3Categories.map((cat) => {
                const baseVal = form.baseYearScope3 ? Number(cat.emissions[form.baseYearScope3] ?? 0) : 0;
                const years = form.nearTermTargetYear && form.baseYearScope3 ? Number(form.nearTermTargetYear) - Number(form.baseYearScope3) : 0;
                
                const ntTarget = cat.includeNt && baseVal > 0 ? Math.max(0, Math.round(baseVal * (1 - 0.025 * years))) : null;
                const ltTarget = cat.includeLt && baseVal > 0 ? Math.max(0, Math.round(baseVal * 0.1)) : null;
                const quiet = baseVal === 0;

                const stickyLtLeft = nearTermRequired ? 45 : 0;
                const stickyCatLeft = nearTermRequired ? 90 : 45;

                return (
                  <tr key={cat.categoryKey}>
                    {nearTermRequired && (
                      <td className="sticky-col-nt">
                        <input
                          type="checkbox"
                          checked={cat.includeNt}
                          disabled={ro}
                          onChange={(e) => handleCategoryToggle(cat.categoryKey, "includeNt", e.target.checked)}
                          style={{ width: 15, height: 15, accentColor: "#3f88a5", cursor: ro ? "default" : "pointer" }}
                        />
                      </td>
                    )}
                    <td className="sticky-col-lt" style={{ left: stickyLtLeft }}>
                      <input
                        type="checkbox"
                        checked={cat.includeLt}
                        disabled={ro}
                        onChange={(e) => handleCategoryToggle(cat.categoryKey, "includeLt", e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: "#3f88a5", cursor: ro ? "default" : "pointer" }}
                      />
                    </td>
                    <td className="sticky-col-cat" style={{ fontWeight: quiet ? 400 : 600, color: quiet ? "#94a3b8" : "#1e293b", textAlign: "left", left: stickyCatLeft }}>
                      <span style={{ color: "#94a3b8", marginRight: 8, fontVariantNumeric: "tabular-nums" }}>
                        {cat.categoryKey.replace("CAT_", "").padStart(2, "0")}
                      </span>
                      {cat.name}
                    </td>
                    
                    {s3YearsList.map((y) => (
                      <td key={y} className={y === Number(form.baseYearScope3) ? "base-year-col" : ""}>
                        <CellField style={{ width: 110 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={fmtNum(cat.emissions[y] ?? "")}
                            onChange={(e) => handleEmissionChange(cat.categoryKey, y, parseNum(e.target.value))}
                            readOnly={ro || !form.baseYearScope3}
                          />
                          <span className="unit">tCO₂e</span>
                        </CellField>
                      </td>
                    ))}
                    
                    {nearTermRequired && (
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: ntTarget === null ? "#94a3b8" : "#1e293b", fontWeight: ntTarget === null ? 400 : 600 }}>
                        {ntTarget !== null ? fmtNum(ntTarget) : "—"}
                      </td>
                    )}
                    
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: ltTarget === null ? "#94a3b8" : "#1e293b", fontWeight: ltTarget === null ? 400 : 600 }}>
                      {ltTarget !== null ? fmtNum(ltTarget) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f8fafc", borderTop: "1.5px solid #cbd5e1" }}>
                {nearTermRequired ? (
                  <td className="sticky-col-nt" style={{ fontWeight: 700 }}>Total</td>
                ) : (
                  <td className="sticky-col-lt" style={{ fontWeight: 700, left: 0 }}>Total</td>
                )}
                {nearTermRequired && <td className="sticky-col-lt" style={{ left: 45 }}></td>}
                <td className="sticky-col-cat" style={{ textAlign: "left", left: nearTermRequired ? 90 : 45 }}></td>
                {s3YearsList.map((y) => (
                  <td key={y} className={y === Number(form.baseYearScope3) ? "base-year-col" : ""} style={{ fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums", paddingRight: 24 }}>
                    {fmtNum(yearlyTotals[y])}
                  </td>
                ))}
                {nearTermRequired && (
                  <td style={{ fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {fmtNum(s3Totals.ntBase)}
                  </td>
                )}
                <td style={{ fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {fmtNum(s3Totals.ltBase)}
                </td>
              </tr>
            </tfoot>
          </S3MatrixTable>
        </S3TableWrap>
        
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
          {nearTermRequired
            ? `Near-term target = base × (1 − 2.5%/yr × years to ${form.nearTermTargetYear || "NT year"}). `
            : ""}
          Long-term target = base × 10% (a 90% cut). Rows with no emissions or not included read as "—".
        </div>
      </Section>
    );
  };

  const ambitionField = () => (
    <SettingsFormGroup>
      <SettingsFormLabel>Ambition Rate (%/yr)<InfoTip text={ambitionHint} /></SettingsFormLabel>
      <RoomyInput
        type="number"
        step="0.1"
        min={ambitionFloor ? Number(ambitionFloor.floorRatePct).toFixed(2) : undefined}
        value={form.ambitionOverridePercentage}
        onChange={(e) => setField("ambitionOverridePercentage", e.target.value)}
        readOnly={ro}
      />
      {ambitionFloor && (
        <AmbitionNote>
          Minimum {Number(ambitionFloor.minRatePct).toFixed(1)}%/yr
          {Number(ambitionFloor.flaPct) > 0 && ` · FLA +${Number(ambitionFloor.flaPct).toFixed(2)}%`}
          {" · required ≥ "}
          <strong>{Number(ambitionFloor.floorRatePct).toFixed(2)}%/yr</strong> (you can enter more)
        </AmbitionNote>
      )}
    </SettingsFormGroup>
  );

  return (
    <SettingLayout
      title="Net Zero"
      subtitle="Set your science-based net-zero targets"
      $wide={true}
    >
      <SubNav activeKey={activeSubTab} onChange={(k) => { setMessage(""); setActiveSubTab(k); }} />
      {activeSubTab === "history" ? (
        <HistoryPanel onEditConfig={handleEditHistoryConfig} />
      ) : (
      <SettingsCard style={{ padding: "32px 36px" }}>
        <Section>
          <SettingsSectionHeader>
            <SectionTitle>Select the scope(s) you want to configure</SectionTitle>
          </SettingsSectionHeader>
          <CheckRow>
            <CheckItem $disabled={ro}>
              <input type="checkbox" checked={form.includeScope12} disabled={ro} onChange={(e) => toggleScope12(e.target.checked)} />
              <span>Scope 1, Scope 2<InfoTip text="Direct emissions (Scope 1) and purchased energy (Scope 2). Set the base year and all target years here." /></span>
            </CheckItem>
            <CheckItem $disabled={ro}>
              <input type="checkbox" checked={form.includeScope3} disabled={ro} onChange={(e) => toggleScope3(e.target.checked)} />
              <span>Scope 3<InfoTip text="Value-chain emissions. You only set its base year — it reuses the Scope 1, Scope 2 target years and ambition." /></span>
            </CheckItem>
            {hasConfig && !editing && (
              <EditButtonSlot>
                <PrimaryButton onClick={() => { setMessage(""); setEditing(true); }}>Edit</PrimaryButton>
              </EditButtonSlot>
            )}
          </CheckRow>
        </Section>

        {(form.includeScope12 || form.includeScope3) && (
          <>
            <SettingsDivider style={{ margin: "34px 0" }} />
            <Section>
              <SettingsSectionHeader>
                <SectionTitle>{form.includeScope12 && form.includeScope3 ? "Base Years" : "Base Year"}</SectionTitle>
              </SettingsSectionHeader>
              <RoomyGrid>
                {form.includeScope12 && yearField(form.includeScope3 ? "Base Year — Scope 1, Scope 2" : "Base Year", "baseYearScope12", "The starting year your Scope 1, Scope 2 emissions are measured against. Must be 2015 or later (SBTi baseline rule).", `2015 – ${currentYear}`, false, null, baseYearError)}
                {form.includeScope3 && yearField(
                  form.includeScope12 ? "Base Year — Scope 3" : "Base Year",
                  "baseYearScope3",
                  "The starting year for your value-chain (Scope 3) emissions. Must be 2015 or later, and on or after the Scope 1, Scope 2 base year.",
                  form.includeScope12 && baseYearValid ? `${form.baseYearScope12} – ${currentYear}` : `2015 – ${currentYear}`,
                  form.includeScope12 && !baseYearValid,
                  "Enter the Scope 1, Scope 2 base year first.",
                  baseYear3Error,
                )}
              </RoomyGrid>
            </Section>

            {form.includeScope12 && s12CollectionSection()}
            {form.includeScope3 && s3CategoryEmissionsSection()}

            {form.includeScope12 && (
              <Section>
                <SettingsSectionHeader>
                  <SectionTitle>Net-Zero Targets</SectionTitle>
                </SettingsSectionHeader>
                <RoomyGrid>
                  {yearField("Net-Zero Commitment Year", "netZeroCommitmentYear", commitHint, commitConstraint, !baseYearValid, "Enter a valid base year first.", commitError)}
                  {yearField("Near-Term Target Year", "nearTermTargetYear", nearTermHint, nearTermConstraint, !commitValid, nearTermDisabledMsg, nearTermError)}
                  {yearField("Net-Zero Target Year", "longTermTargetYear", netZeroHint, netZeroConstraint, !nearTermValid, "Enter the near-term target year first.", netZeroError)}
                  {ambitionField()}
                </RoomyGrid>
              </Section>
            )}
          </>
        )}

        {editing && (
          <>
            <SaveNote>
              <i className="fas fa-info-circle" />
              <span>
                Saving stores a <b>new active version</b>. Your current config moves to <b>History</b>,
                where you can re-enable it anytime.
              </span>
            </SaveNote>
            <Actions>
              <PrimaryButton onClick={handleSave} disabled={saving || hasFieldErrors}>
                {saving ? "Saving…" : "Save"}
              </PrimaryButton>
              {hasConfig && <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>}
            </Actions>
          </>
        )}

        {message && <div style={{ marginTop: 16, fontSize: 13, color: "#475569" }}>{message}</div>}
      </SettingsCard>
      )}

      {showConfirm && (
        <Overlay onClick={() => setShowConfirm(false)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogHead>
              <h3>Confirm net-zero configuration</h3>
              <p>Review the values below before saving. This stores a new active version; the current config moves to History.</p>
            </DialogHead>
            <SummaryBody>
              <SummaryRow>
                <span className="k">Scopes</span>
                <span className="v">{[form.includeScope12 && "Scope 1, Scope 2", form.includeScope3 && "Scope 3"].filter(Boolean).join(" + ")}</span>
              </SummaryRow>
              {form.includeScope12 && (
                <SummaryRow><span className="k">Base year — Scope 1, Scope 2</span><span className="v">{form.baseYearScope12 || "—"}</span></SummaryRow>
              )}
              {form.includeScope3 && (
                <SummaryRow><span className="k">Base year — Scope 3</span><span className="v">{form.baseYearScope3 || "—"}</span></SummaryRow>
              )}
              {form.includeScope12 && (
                <>
                  <SummaryRow><span className="k">Commitment year</span><span className="v">{form.netZeroCommitmentYear || "—"}</span></SummaryRow>
                  <SummaryRow><span className="k">Near-term target</span><span className="v">{form.nearTermTargetYear || "—"}</span></SummaryRow>
                  <SummaryRow><span className="k">Net-zero target</span><span className="v">{form.longTermTargetYear || "—"}</span></SummaryRow>
                  <SummaryRow><span className="k">Ambition rate</span><span className="v">{form.ambitionOverridePercentage !== "" ? `${form.ambitionOverridePercentage}%/yr` : "—"}</span></SummaryRow>
                </>
              )}
              {(gapYears1.length > 0 || gapYears2.length > 0 || gapYears3.length > 0) && (
                <>
                  <SummaryGroupLabel>Collected data years (no-data years)</SummaryGroupLabel>
                  {gapYears1.length > 0 && (
                    <SummaryRow $top><span className="k">Scope 1</span><YearChips className="v">{gapYears1.map((y) => <span key={y}>{y}</span>)}</YearChips></SummaryRow>
                  )}
                  {gapYears2.length > 0 && (
                    <SummaryRow $top><span className="k">Scope 2</span><YearChips className="v">{gapYears2.map((y) => <span key={y}>{y}</span>)}</YearChips></SummaryRow>
                  )}
                  {gapYears3.length > 0 && (
                    <SummaryRow $top><span className="k">Scope 3</span><YearChips className="v">{gapYears3.map((y) => <span key={y}>{y}</span>)}</YearChips></SummaryRow>
                  )}
                </>
              )}
            </SummaryBody>
            <DialogActions>
              <SecondaryButton onClick={() => setShowConfirm(false)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={confirmSave} disabled={saving}>{saving ? "Saving…" : "Save config"}</PrimaryButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}

    </SettingLayout>
  );
};

export default NetZero;
